import type { AxiosResponse } from 'axios'
import type { IRoiMetrics,
  IRoiSummary, IScanMapPoint } from '@repo/types'

import type { GeoPoint } from 'utils/geoRing'

export enum Actions {
  GET_ANALYTICS_TOTALS = 'GET_ANALYTICS_TOTALS',
  SET_ANALYTICS_TOTALS = 'SET_ANALYTICS_TOTALS',

  GET_ANALYTICS_STATISTICS = 'GET_ANALYTICS_STATISTICS',
  SET_ANALYTICS_STATISTICS = 'SET_ANALYTICS_STATISTICS',

  GET_ANALYTICS_SCANS_MAP = 'GET_ANALYTICS_SCANS_MAP',
  SET_ANALYTICS_SCANS_MAP = 'SET_ANALYTICS_SCANS_MAP',

  GET_ANALYTICS_ROI = 'GET_ANALYTICS_ROI',
  SET_ANALYTICS_ROI = 'SET_ANALYTICS_ROI',

  /**
   * ── A SECOND ROI SLOT, AND IT IS A CORRECTNESS REQUIREMENT ────────────────────────
   *
   * The hero owns its own period (default: the current billing period, where the spend
   * denominator is EXACT) while the ROI metric cards keep following the page's date
   * range. Two ranges means two requests to `/analytics/admin/roi`.
   *
   * They CANNOT share `roi`. `SET_ANALYTICS_ROI` overwrites that slot wholesale, so two
   * concurrent fetches with different ranges race into it — last saga to resolve wins,
   * both consumers read the winner, and the hero silently reports a multiple measured
   * over the cards' window (or vice versa). Non-deterministically, so it would pass a
   * casual look. This is the same failure the KPI tiles' prior-period comparison was
   * moved onto the server to avoid; see `compare_to_previous` in `GetAnalyticsTotalsData`.
   *
   * Same endpoint, same saga shape, separate slot and separate `takeLatest` channel —
   * so each request can only ever clobber another request for the SAME consumer.
   */
  GET_ANALYTICS_ROI_HERO = 'GET_ANALYTICS_ROI_HERO',
  SET_ANALYTICS_ROI_HERO = 'SET_ANALYTICS_ROI_HERO',

  SET_ANALYTICS_FILTERS = 'SET_ANALYTICS_FILTERS',
}

/**
 * Which primary metric a panel counts — for the MAP and the LEADERBOARD.
 *
 * ⚠️ NARROWER THAN THE CHART'S UNION, deliberately (VERA-260). This is the set of
 * dimensions the scans-map endpoint's `mode` accepts and the leaderboard endpoint can
 * group by, and it is also the key of `LEGACY_PANEL_METRIC_ID` — the map from a legacy
 * panel id to the metric card it resolves to. Widening it to cover every chartable
 * dimension would force that record to answer "which legacy panel was Invalid Scans",
 * a question with no answer.
 *
 * The chart plots a strictly larger set; see `AnalyticsSeriesDimension` below, of which
 * this is a subset (asserted, not merely asserted-in-prose).
 */
export type AnalyticsDataType = 'scans' | 'certificates'

/** Global filter state for the analytics page (shared by Overview, ScansByTime, and future certificate pages) */
export type AnalyticsFilters = {
  /** Owner (user) IDs to scope results to — UI populated, backend TODO(VERA-260) */
  ownerIds: string[]
  /** Product IDs to scope results to — UI populated, backend TODO(VERA-260) */
  productIds: string[]
  /** Certificate IDs to scope results to — UI populated, backend TODO(VERA-260) */
  certificateIds: string[]
  /** Certificate statuses — fully wired end-to-end */
  certificateStatuses: string[]
  /**
   * ── THE SCAN'S OWN OUTCOME (VERA-260) — a SECOND status axis ──────────────────────
   *
   * `'verified' | 'invalid' | 'tampered'` ("Opened"), empty meaning no narrowing. It sits
   * BESIDE `certificateStatuses` rather than inside it because the two describe different
   * entities: a CERTIFICATE is Created / Active / Lost / Stolen, a SCAN is Verified /
   * Invalid / Opened. They are orthogonal, and the query the page exists to answer —
   * "invalid scans of active certificates" — needs both at once. Merged into one
   * multi-select, selecting a member of each has no defensible reading: as an AND it is
   * that query, as an OR it is a union of two unrelated sets, and one control cannot say
   * which it did.
   *
   * ⚠️ This was CARD-LOCAL `useState` in `ScansByLocation` first, filtering points the map
   * had already fetched. That file's own note explains why it stayed there at the time:
   * promoting it would either be a lie (a page-level chip claiming the page is filtered
   * when only the map is) or "a much larger change — adding a real scan-validity dimension
   * to four endpoints". This is that larger change. The honest accounting at the time was
   * that only ONE of the four could take it; VRG-452 changed the count, not the test:
   *
   *   - scans-map -> WIRED from the start. It reads the `scans` table, so `invalid` /
   *                  `tampered` are columns on the very rows being plotted.
   *   - totals / granular -> NOW WIRED. They were NOT, for exactly the reason below,
   *                  until VRG-452-Scans.Data.Source.Divergence moved their scan counts
   *                  off `certificate_action_logs` and onto `scans`. The filter followed
   *                  the table. `top-influencers` and `countries` were built reading
   *                  `scans` and are wired too.
   *   - ROI -> STILL NOT wired, and not deferred work either. Its consumer-touchpoint,
   *                  repeat-scan and product-lifespan figures count
   *                  `certificate_action_logs` rows, which carry no scan-outcome columns
   *                  and no row-level link to a `scans` row (only `certificate_id`), so
   *                  the nearest predicate available is "every scan of a certificate that
   *                  has at least one matching scan" — which for an OUTCOME is not an
   *                  approximation, it is a different question. `/roi` does not declare
   *                  the query prop, so sending it would be stripped anyway. See
   *                  `getScanMapPoints` in `analytics.postgres.repository.ts` for the
   *                  full argument.
   *
   * `certificateStatuses` still has the partial reach this one used to have — the totals
   * endpoint does not accept it — so that gap outlived this one and is now the page's
   * remaining filter-reach inconsistency.
   *
   * ⚠️ NOT sent by any call site. `useAnalyticsScansMap` injects it from here before
   * dispatching, exactly as `useAnalyticsAreaParam` does for `area` and for the same
   * reason (§42): an opt-in every call site must remember is a latent bug, not a
   * convention.
   */
  scanStatuses: string[]
  /** Date range for the query */
  dateRange: {
    from: Date
    to: Date
  }
  /*
   * ── ⚠️ `granularity` WAS A FIELD HERE AND IT IS DELETED (VRG-512) ────────────────────
   *
   * 🚨 Kept as a note because the arrangement was reasonable-looking and wrong in exactly
   * the way `chartDataType` below records, and the next person to want "one bucket for the
   * page" will find this the natural place to put it back.
   *
   * It held the "View by:" toolbar control's chosen bucket width, page-wide. What made it
   * wrong is the same SCOPE-vs-POSITION mismatch the pill below was demoted for: the date
   * range beside it in that toolbar is genuinely global — it scopes every KPI tile, both
   * panels and the ROI cards — while the bucket scoped only the chart cards, and it scoped
   * ALL of them at once. Measured on a canvas holding a Scans chart and a Certificates
   * chart, one "View by" change fired two requests and re-bucketed both, so a reader
   * narrowing one chart to daily detail silently re-drew a neighbour they had not touched.
   *
   * The bucket is a DISPLAY property, not a data property: the date range answers "what am
   * I looking at", while line-vs-bar and day-vs-month both answer "how is this one series
   * drawn". So it now lives per card, keyed by card id, beside the mark toggle it is a
   * sibling of — `components/analytics/analyticsCardGranularity`, whose header carries the
   * full argument including why a MODULE store rather than component state.
   *
   * ⚠️ THE ONE REAL ARGUMENT FOR HAVING IT HERE IS ANSWERED, NOT IGNORED. This field's
   * docblock argued the unit could not live in the chart because the CSV export has to send
   * the bucket the chart drew, and `analyticsExport` is a plain async function with no
   * access to component state. True — and satisfied by a module store, which it reads per
   * card id (`analyticsCardGranularityOverride`). Component state would still be wrong.
   */
  /** Geo country codes (ISO-2) — UI populated, backend TODO(VERA-260) */
  location: string[]
  /**
   * ── THE DRAWN AREA (VRG-480) — A COORDINATE PREDICATE, NOT A PLACE ────────────────
   *
   * A ring drawn on the map card, filtering scans by their own `geo_lat` / `geo_lng`. It
   * never resolves to a country or a city, which is precisely why it sits BESIDE
   * `location` rather than inside it: `location` constrains `scans.geo_country`, this
   * constrains the same row's coordinates, and two predicates over two columns AND with
   * nothing to reconcile. The pins already reflect the country filter, so drawing
   * narrows that set further.
   *
   * ⚠️ It is page-global, exactly like `location`, even though the only control that can
   * SET it lives on the map card. That asymmetry is the reason the chip matters: the
   * chip row is where a page-wide constraint becomes visible, and it is the only surface
   * that says so when the map card is not on the canvas.
   *
   * `null` rather than `[]` for "no area". An empty ring and a drawn ring are different
   * kinds of thing (a ring is only meaningful at 3+ vertices), and `null` makes
   * "is there an area" a single test rather than a length check that could be satisfied
   * by a degenerate one- or two-point value.
   *
   * NOT persisted here in wire form — see `encodeRing`. Redux holds points because the
   * map has to REDRAW the shape; the string exists only at the request and saved-view
   * boundaries.
   */
  area: GeoPoint[] | null
  /**
   * ── THE DIMENSION IS PER-PANEL, AND IT USED TO BE ONE GLOBAL `dataType` ───────────
   *
   * 🚨 The diagnosis, kept because the arrangement it replaces was reasonable-looking
   * and wrong, and the next person to see two near-identical keys will want to
   * "consolidate" them back.
   *
   * There was ONE `dataType`, driven by a Scans / Certificates segmented pill sitting in
   * the page header beside the date picker. It was a PER-CARD control that got promoted
   * to global for the only reason that ever promotes one: exactly two cards happened to
   * want it, and two is enough to look like "the page".
   *
   * What made it wrong is a mismatch between SCOPE and POSITION. The date range beside
   * it is genuinely global — it scopes every KPI tile, both panels and the ROI cards.
   * The dimension scoped only the chart and the map: the four KPI tiles ignored it
   * entirely, so `Certificates` left "Total scans" reading a scan count directly beneath
   * a control that appeared to say the page was counting certificates. Two controls in
   * one row, one global and one not, with nothing on screen distinguishing them.
   *
   * It survived because no third card ever wanted a different dimension, which is a fact
   * about the card set rather than an argument about the control.
   *
   * ⚠️ THE TWO ARE INDEPENDENT, NOT LINKED, and that is the whole point of the move.
   * Keeping one shared value while rendering the control in each card header would be
   * strictly worse than leaving it global: a control drawn INSIDE card A that silently
   * changes card B is a more misleading position than a page-level control that changes
   * both, because the page-level one was at least honest about its reach. If these are
   * ever re-merged, the control has to go back to the page header with them.
   */
  /** What the "…by time" chart counts. Set from that card's own header. */
  chartDataType: AnalyticsDataType
  /** What the "…by location" map counts. Set from that card's own header. */
  mapDataType: AnalyticsDataType
}

export type GetAnalyticsTotalsCounts = {
  certificates_count: number
  scans_count: number
  transfers_count: number
  completed_transfers_count: number
  in_progress_transfers_count: number
}

export type GetAnalyticsTotalsData = GetAnalyticsTotalsCounts & {
  /**
   * The immediately preceding window of the same length. Present ONLY when the
   * request set `compare_to_previous` — absent, not zeroed, otherwise, so a caller
   * that did not ask for a comparison cannot accidentally render one against zeros.
   */
  previous?: GetAnalyticsTotalsCounts
  /**
   * The window `previous` was actually measured over. `to` is the EXCLUSIVE bound and
   * always equals the current window's `from`, so no instant falls in both windows or
   * in neither.
   *
   * Returned so a delta can be LABELLED with its comparison period rather than shown
   * as a bare percentage. A "+40%" with no stated basis is the kind of figure that gets
   * screenshotted and misread, which on this product is the expensive class of mistake.
   */
  previous_range?: { from: string; to: string }
}

export type GetAnalyticsStatisticsData = {
  timestamp: string
  certificates_count: number
  certificates_detailed: CertificatesDetailed[]
  scans_count: number
  scans_detailed: ScansDetailed[]
  transfers_count: number
  transfers_detailed: TransfersDetailed[]
  /*
    ── VERA-260: FOUR COUNTS WITH NO `_detailed` TWIN ──────────────────────────────────

    Each sums, across the whole range, to the ROI card of the same name — verified
    against Postgres when they were added (invalid 248, resales 22, stolen 3, lost 2).
    That invariant is what makes them plottable: a series whose total disagreed with the
    card beside it would be worse than no series at all.

    ⚠️ STILL DO NOT DERIVE AN INVALID-SCAN RATE FROM `invalid_scans_count / scans_count`.
    The two were counted from different populations when this was written (the `scans`
    table vs. the certificate action log); VRG-452-Scans.Data.Source.Divergence moved
    `scans_count` onto `scans`, so they now share a table — but not a population.
    `invalid_scans_count` also counts CERTIFICATE-LESS invalid scans (a tag Vera never
    issued), which `scans_count` cannot carry because it is broken down by certificate
    status. So the ratio is still across two sets and can still exceed 1. The ROI
    endpoint returns `invalid_scan_rate` with its own denominator, from one CTE over one
    population, precisely so a chart never has to divide these.

    ⚠️ NO PER-STATUS BREAKDOWN, and that is a property of the measurement rather than a
    gap. A certificate STATUS is not a fact about a rejected scan or a completed resale,
    so `ScansByTime2` draws these as a single total series — see `DETAILED_FIELD` there.
  */
  invalid_scans_count: number
  stolen_reports_count: number
  lost_reports_count: number
  resales_count: number
  /*
    ── SIX STATISTICS THAT ARE NOT COUNTS (VERA-260) ───────────────────────────────────

    The client mirror of `IGranularStatistic`. Everything above this line is additive and
    sums, over the range, to its own ROI card. ⚠️ THESE DO NOT, and the surface has to say
    so rather than let a reader infer a broken chart — see `ANALYTICS_VALUE_RECONSTRUCTION`
    in `analyticsDisplay`, which classifies each one and is what the description and the
    tooltip are built from.

    ⚠️ THE NESTED SHAPE IS LOad-BEARING, NOT TIDINESS. `AnalyticsSeriesCountField` below is
    derived as "every member of this type that `extends number`". A flat
    `invalid_scan_rate: number` would enrol these six in the COUNT union, and every total
    `Record` keyed on it — `SERIES_COUNT_FIELDS`, `CHART_RENDERABLE`, `MAP_RENDERABLE`,
    `MAP_LOCATED`, `TABLE_RANKABLE`, `TOTAL_COUNT_FIELD`, `DETAILED_FIELD`,
    `DIMENSION_SERIES_LABEL` — would demand an answer for a dimension that is not a count.
    "Can the map plot the invalid scan RATE" has no honest answer; a pin is present or
    absent, so 4% and 96% draw the same picture. Nesting keeps the two unions disjoint by
    construction and lets each stay total over exactly its own kind. The assertion below
    (`VALUE_FIELDS_ARE_NOT_COUNT_FIELDS`) is that claim in enforceable form.
  */
  invalid_scan_rate: AnalyticsBucketStatistic
  repeat_scan_rate: AnalyticsBucketStatistic
  avg_resale_price_cents: AnalyticsBucketStatistic
  median_days_to_first_scan: AnalyticsBucketStatistic
  certificates_per_owner: AnalyticsBucketStatistic
  resales_per_resold_item: AnalyticsBucketStatistic
}

/**
 * One non-count statistic on one bucket. The client mirror of the API's
 * `IGranularStatistic`; read that interface for why all three fields are required.
 *
 * The short version, because each field maps to a specific defect this page can ship:
 *  - `value: null` is an UNDEFINED statistic (no observations), never a zero. A bucket
 *    with no scans has no invalid-scan rate, and a `0` there reads as "0% invalid".
 *  - `denominator` is how the surface tells a low-confidence point from a real one AND
 *    tells a SUPPRESSED bucket from an EMPTY one — two different facts that would
 *    otherwise both render as the same bare gap.
 *  - `numerator` is what makes the tooltip say "3 of 47" instead of "6.4%".
 */
export type AnalyticsBucketStatistic = {
  value: number | null
  /** `null` for a median — an order statistic has no numerator. */
  numerator: number | null
  denominator: number
}

type CertificatesDetailed = {
  count: number
  company_id: string
  status: string
}

type ScansDetailed = {
  count: number
  company_id: string
}

type TransfersDetailed = {
  count: number
  company_id: string
}

/**
 * The dated count fields of one statistics bucket, derived from the response TYPE.
 *
 * `GetAnalyticsStatisticsData` is one row per bucket: a `timestamp`, plus counts, plus
 * per-company breakdown arrays. Its NUMERIC members are precisely the quantities that can
 * be plotted over that timestamp, so this mapped type is not a description of the sink —
 * it IS the sink.
 */
export type AnalyticsSeriesCountField = {
  [K in keyof GetAnalyticsStatisticsData]-?: GetAnalyticsStatisticsData[K] extends number
    ? K
    : never
}[keyof GetAnalyticsStatisticsData]

/** `'scans_count'` -> `'scans'`, at the type level. The runtime twin is `stripCount`. */
type StripCountSuffix<K extends string> = K extends `${infer D}_count` ? D : never

/**
 * ── EVERY DIMENSION THE TIME CHART CAN PLOT (VERA-260) ──────────────────────────────
 *
 * DERIVED from the statistics response, never authored. Add a `*_count` to the bucket row
 * above and this union widens by itself — which is what makes the total `Record`s keyed on
 * it (`CHART_RENDERABLE` in `analyticsDisplay`, `TOTAL_COUNT_FIELD` and `DETAILED_FIELD` in
 * `ScansByTime2`) stop compiling until the new dimension has been classified. A hand-written
 * union would go stale in the direction that lies: the server gains a series, the union does
 * not, and the affordance never appears with nothing to say why.
 *
 * ⚠️ SEPARATE FROM `AnalyticsDataType`, which is its SUBSET rather than its equal. The
 * narrow union is what the map, the leaderboard and `LEGACY_PANEL_METRIC_ID` are keyed on,
 * and each of those would be forced into a false answer by a dimension it cannot serve.
 * Keying the chart on the wider union and leaving those three on the narrow one is what
 * lets the two disagree — and that disagreement is information, not debt.
 */
export type AnalyticsSeriesDimension = StripCountSuffix<AnalyticsSeriesCountField>

/**
 * ⚠️ TYPE-LEVEL ASSERTION, not a comment: the map/leaderboard union must stay a SUBSET of
 * the chart's. Every consumer that narrows a dimension to `AnalyticsDataType` and hands it
 * to the chart relies on this; if `AnalyticsDataType` ever gains a value with no matching
 * `*_count` on the bucket row, this stops compiling here rather than producing a chart that
 * silently plots `undefined`.
 */
type DataTypeIsASeriesDimension = AnalyticsDataType extends AnalyticsSeriesDimension
  ? true
  : 'AnalyticsDataType has a value the statistics response carries no *_count for'
const DATA_TYPE_IS_A_SERIES_DIMENSION: DataTypeIsASeriesDimension = true
void DATA_TYPE_IS_A_SERIES_DIMENSION

/* ────────────────────────────────────────────────────────────────────────────────────
   THE SECOND SINK: A VALUE PER BUCKET (VERA-260)
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The non-count statistic fields of one bucket, derived from the response TYPE.
 *
 * Exactly parallel to `AnalyticsSeriesCountField` above and derived the same way — by the
 * MEMBER TYPE rather than by a hand-written list — so a statistic added to the bucket row
 * widens this union by itself and every total `Record` keyed on it stops compiling until
 * the new one is classified. That is the same discharge-by-compiler `analyticsDisplay`'s
 * header describes, applied to the axis that this change adds.
 *
 * The two unions are DISJOINT by construction, which is the whole reason a statistic is an
 * object rather than a number; see the assertion immediately below.
 */
export type AnalyticsSeriesValueField = {
  [K in keyof GetAnalyticsStatisticsData]-?: GetAnalyticsStatisticsData[K] extends AnalyticsBucketStatistic
    ? K
    : never
}[keyof GetAnalyticsStatisticsData]

/**
 * ⚠️ TYPE-LEVEL ASSERTION, not a comment: no field is BOTH a count and a value.
 *
 * This is the property the nested shape exists to guarantee, so it is asserted rather than
 * trusted. If a statistic is ever flattened to a bare `number` — or a numerator is hoisted
 * to the top level as `invalid_rate_numerator: number`, which is the tempting version —
 * it enrols in `AnalyticsSeriesCountField`, and `SERIES_COUNT_FIELDS` and the six other
 * total records keyed on the count union start demanding chart/map/table answers for a
 * quantity that is not a count. This stops compiling at that moment, which is where a
 * reader can still see what happened, instead of at a `MAP_RENDERABLE` entry that someone
 * fills in with `false` to make the build pass.
 */
type ValueFieldsAreNotCountFields = AnalyticsSeriesValueField &
  AnalyticsSeriesCountField extends never
  ? true
  : 'A bucket field is being read as both a count and a statistic — see AnalyticsBucketStatistic'
const VALUE_FIELDS_ARE_NOT_COUNT_FIELDS: ValueFieldsAreNotCountFields = true
void VALUE_FIELDS_ARE_NOT_COUNT_FIELDS

/**
 * ── EVERY DIMENSION THE TIME CHART CAN PLOT AS A VALUE (VERA-260) ───────────────────
 *
 * The value union's counterpart to `AnalyticsSeriesDimension`, and — unlike that one —
 * the field name IS the dimension. There is no suffix to strip: `_count` is a suffix
 * because a count of a THING has a thing to name, whereas `invalid_scan_rate` names the
 * statistic itself and `stripCount` on it would silently return the field unchanged and
 * invent a dimension called "invalid scan rate" that no record is keyed on.
 *
 * ⚠️ DISJOINT FROM `AnalyticsSeriesDimension`, and it must stay that way: the two are used
 * as alternatives (`analyticsCardDimension` returns one or the other), so an id present in
 * both would resolve to whichever branch happened to be checked first.
 */
export type AnalyticsValueDimension = AnalyticsSeriesValueField

export type GetAnalyticsTotalsDataResponse = {
  data: GetAnalyticsTotalsData
  query: GetAnalyticsStatisticsPayload
}

export type GetAnalyticsStatisticsDataResponse = {
  data: GetAnalyticsStatisticsData[]
  query: GetAnalyticsTotalsPayload
}

export type GetAnalyticsTotalsPayload = {
  from: Date
  to: Date
  company_ids: string[]
  owner_ids?: string[]
  product_ids?: string[]
  certificate_ids?: string[]
  /**
   * The drawn area (VRG-480), already encoded as `lat,lng;lat,lng;…`.
   *
   * ⚠️ NOT set by call sites. Every analytics hook injects it from
   * `state.analytics.filters.area` before dispatching — see `useAnalyticsAreaParam` for
   * why it is derived rather than threaded through each payload literal.
   */
  area?: string
  location?: string[]
  /**
   * The SCAN's own outcome (VERA-260) — `verified` / `invalid` / `tampered`.
   *
   * ⚠️ NOT set by call sites, exactly like `area` below: the hook injects it from
   * `state.analytics.filters.scanStatuses` before dispatching. See
   * `useAnalyticsScanStatusParam`.
   */
  scan_statuses?: string[]
  /**
   * Also return the immediately preceding window of the same length, as `previous` /
   * `previous_range`, so a KPI tile can render a trend delta.
   *
   * It is a request flag rather than a second client-side query for a specific reason:
   * `useAnalyticsTotals` reads `state.analytics.totals`, a SINGLE redux slot that the
   * reducer overwrites wholesale. Two concurrent fetches with different ranges race into
   * that one slot — last saga to resolve wins and BOTH consumers read it — so the
   * comparison has to arrive in the same response as the thing it is compared against.
   *
   * The saga forwards the payload wholesale as query params, so nothing else is needed
   * to carry it.
   */
  compare_to_previous?: boolean
}

export type GetAnalyticsTotalsResponse = AxiosResponse<GetAnalyticsTotalsDataResponse>

export type GetAnalyticsStatisticsPayload = {
  from: Date
  to: Date
  company_ids: string[]
  certificate_statuses?: string[]
  interval_count?: number
  interval_granularity?: 'hour' | 'day' | 'month'
  owner_ids?: string[]
  product_ids?: string[]
  certificate_ids?: string[]
  location?: string[]
  /**
   * The SCAN's own outcome (VERA-260) — `verified` / `invalid` / `tampered`.
   *
   * ⚠️ NOT set by call sites, exactly like `area` below: the hook injects it from
   * `state.analytics.filters.scanStatuses` before dispatching. See
   * `useAnalyticsScanStatusParam`.
   */
  scan_statuses?: string[]
  /**
   * The drawn area (VRG-480), already encoded as `lat,lng;lat,lng;…`.
   *
   * ⚠️ NOT set by call sites. Every analytics hook injects it from
   * `state.analytics.filters.area` before dispatching — see `useAnalyticsAreaParam` for
   * why it is derived rather than threaded through each payload literal.
   */
  area?: string
}

export type GetAnalyticsStatisticsResponse = AxiosResponse<GetAnalyticsStatisticsDataResponse>

export type GetAnalyticsScansMapPayload = {
  from: Date
  to: Date
  company_ids?: string[]
  owner_ids?: string[]
  product_ids?: string[]
  certificate_ids?: string[]
  location?: string[]
  certificate_statuses?: string[]
  /**
   * The SCAN's own outcome (VERA-260) — `verified` / `invalid` / `tampered`.
   *
   * ⚠️ NOT set by call sites, exactly like `area` below: `useAnalyticsScansMap` injects it
   * from `state.analytics.filters.scanStatuses` before dispatching. See
   * `useAnalyticsScanStatusParam`.
   */
  scan_statuses?: string[]
  /**
   * The drawn area (VRG-480), already encoded as `lat,lng;lat,lng;…`.
   *
   * ⚠️ NOT set by call sites. Every analytics hook injects it from
   * `state.analytics.filters.area` before dispatching — see `useAnalyticsAreaParam` for
   * why it is derived rather than threaded through each payload literal.
   */
  area?: string
  /** 'all' = every located scan; 'latest' = one point per certificate (most recent). */
  mode?: 'all' | 'latest'
}

export type GetAnalyticsScansMapDataResponse = {
  data: IScanMapPoint[]
  query: GetAnalyticsScansMapPayload
}

/**
 * ── WHY THE SCANS-MAP SLOT IS KEYED AND THE OTHERS ARE NOT ─────────────────────────
 *
 * The page can now hold MORE THAN ONE MAP CARD at a time — the legacy `scans_by_location`
 * panel plus any number of per-metric map variants (VRG-467) — and their requests differ:
 * a certificates-dimension card asks for `mode: 'latest'` (one point per certificate)
 * while a scans one asks for `mode: 'all'`.
 *
 * With one slot and `takeLatest` that arrangement does not degrade, it BREAKS, in two
 * compounding ways. `takeLatest` cancels the earlier request outright, so the first card
 * to dispatch never receives a response at all; and the single slot is overwritten
 * wholesale, so every card then reads whichever response did land. Observed live before
 * this change: adding a certificates map variant made the legacy "Scans by location"
 * card silently switch from 336 located scans to 21 certificate points, under a heading
 * that still said scans.
 *
 * This is the third instance of one hazard in this reducer. `SET_ANALYTICS_ROI` carries
 * the same note, and the ROI hero was given a SECOND SLOT for exactly this reason. A
 * second slot works when there are two known consumers; it does not when the number of
 * consumers is whatever the user has dragged onto the canvas. So this one is keyed by
 * its request instead, which is the general form of the same fix.
 *
 * The key is the serialised payload, so two cards asking the same question share one
 * entry (and one request) while two cards asking different questions cannot see each
 * other's answer. `analyticsScansMapKey` is the single definition of it — the hook that
 * reads and the saga that writes must agree, and a second stringification somewhere is
 * how they would stop agreeing.
 *
 * ⚠️ The chart is NOT affected and does NOT need this. Two chart cards issue byte-
 * identical `GetAnalyticsStatisticsPayload`s: the response carries `scans_count` AND
 * `certificates_count` for every bucket, and `dataType` only picks which one
 * `ScansByTime2` reads, client-side. So `takeLatest` collapsing two chart requests is
 * correct there rather than lossy. If a dimension ever reaches the statistics REQUEST,
 * this note stops being true and that slot needs the same treatment.
 */
export type AnalyticsScansMapCache = Record<string, GetAnalyticsScansMapDataResponse>

/**
 * The cache key for one scans-map request.
 *
 * `JSON.stringify` over the payload the caller built. Dates serialise to ISO, and the
 * hook hands the SAME object literal to this function and to the action, so the key the
 * consumer reads and the key the saga writes are byte-identical by construction rather
 * than by two implementations happening to agree.
 *
 * Property ORDER matters to `JSON.stringify` and is therefore load-bearing: it is stable
 * only because there is one construction site (`ScansByLocation`). A second caller
 * building the same fields in a different order would key the same request twice —
 * wasteful, not incorrect, and the reason this function is exported rather than inlined.
 */
export const analyticsScansMapKey = (payload?: GetAnalyticsScansMapPayload): string =>
  JSON.stringify(payload ?? {})

export type GetAnalyticsScansMapResponse = AxiosResponse<GetAnalyticsScansMapDataResponse>

export type GetAnalyticsRoiPayload = {
  from: Date
  to?: Date
  /**
   * Scopes the request to one payer. NOT optional in practice for the hero: the API
   * returns `roi` only when the request names exactly ONE company, and omits it
   * otherwise rather than summing bills across brands.
   *
   * It was missing from this type while the page was already sending it — the saga
   * passes `payload` straight through as query params, so the field reached the API and
   * only the type disagreed.
   */
  company_ids?: string[]
  owner_ids?: string[]
  product_ids?: string[]
  certificate_ids?: string[]
  /**
   * The drawn area (VRG-480), already encoded as `lat,lng;lat,lng;…`.
   *
   * ⚠️ NOT set by call sites. Every analytics hook injects it from
   * `state.analytics.filters.area` before dispatching — see `useAnalyticsAreaParam` for
   * why it is derived rather than threaded through each payload literal.
   */
  area?: string
  location?: string[]
  /**
   * Also return `roi.value_series` — value delivered bucketed over the range — for the
   * hero's collapsible chart. Omitted by the ROI CARDS' request, which has no chart.
   *
   * The server picks the bucket granularity from the range length and reports it back
   * as `roi.value_series_granularity`; the client does not get to ask for one. See the
   * API's own note — a client-chosen granularity lets a three-year range request ~1100
   * daily buckets.
   */
  value_series?: boolean
  /**
   * Also return `roi.spend_series` — the DENOMINATOR over the same buckets, built from
   * settled Stripe invoices.
   *
   * ⚠️ IMPLIES `value_series` on the server, and the two must be sent together rather
   * than relying on that: the spend points reuse the value series' bucket keys, and each
   * point's `running_multiple` divides the cumulative VALUE by the cumulative spend. A
   * request for spend alone would be asking for a numerator-less ratio.
   */
  spend_series?: boolean
}

export type GetAnalyticsRoiDataResponse = {
  data: IRoiMetrics
  /**
   * The ROI hero. Absent when the request is not scoped to exactly one company —
   * spend belongs to one payer, so a multi-company request has no denominator.
   */
  roi?: IRoiSummary
  query: GetAnalyticsRoiPayload
}

export type GetAnalyticsRoiResponse = AxiosResponse<GetAnalyticsRoiDataResponse>
